import React, { useState } from 'react';
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  IconButton,
  Step,
  StepContent,
  StepLabel,
  Stepper,
  Tooltip,
  Typography,
} from '@mui/material';
import { ContentCopy as CopyIcon, Check as CheckIcon } from '@mui/icons-material';

/**
 * How to bring a new domain controller under management.
 *
 * Written for someone who has a DC and nothing else. The order matters and is
 * not obvious: the enrollment token is SINGLE-USE and short-lived, so it is
 * minted last, after the host is ready to consume it — minting it first is how
 * people end up with an expired token and a half-installed agent.
 */

function Cmd({ text, label }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(true);
      setTimeout(() => setCopied(false), 1600);
    } catch {
      /* clipboard blocked outside a secure context; the text is selectable */
    }
  };
  return (
    <Box sx={{ mb: 1.5 }}>
      {label && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
          {label}
        </Typography>
      )}
      <Box
        sx={{
          position: 'relative',
          bgcolor: 'action.hover',
          border: 1,
          borderColor: 'divider',
          borderRadius: 1,
          p: 1.25,
          pr: 5,
          fontFamily: 'monospace',
          fontSize: '0.78rem',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-word',
        }}
      >
        {text}
        <Tooltip title={copied ? 'Copied' : 'Copy'}>
          <IconButton size="small" onClick={copy} sx={{ position: 'absolute', top: 4, right: 4 }}>
            {copied ? <CheckIcon fontSize="small" color="success" /> : <CopyIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
      </Box>
    </Box>
  );
}

function AgentDeployGuide({ open, onClose, hubUrl }) {
  const hub = hubUrl || '<AGENT_HUB_URL is not set on this deployment>';

  const steps = [
    {
      label: 'Create a service account on the domain controller',
      body: (
        <>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            The agent runs unprivileged. It never logs in as root and never gets a shell.
          </Typography>
          <Cmd
            text={`sudo useradd --system --home /var/lib/samba-agent \\
  --shell /usr/sbin/nologin samba-agent
sudo install -d -o samba-agent -g samba-agent -m 0700 /var/lib/samba-agent
sudo install -d -o root -g samba-agent -m 0750 /etc/samba-agent`}
          />
        </>
      ),
    },
    {
      label: 'Install the binary and its allowlist',
      body: (
        <>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Build on a host matching the DC&apos;s distribution, then confirm the blast radius
            from the binary itself rather than from documentation.
          </Typography>
          <Cmd
            text={`sudo install -o root -g root -m 0755 samba-agent /usr/local/bin/samba-agent
/usr/local/bin/samba-agent allowlist`}
          />
          <Typography variant="caption" color="text.secondary">
            Expect 41 operations, with Destructive / Schema / Service marked “requires approval”.
          </Typography>
        </>
      ),
    },
    {
      label: 'Give it directory credentials',
      body: (
        <>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            Two binds, deliberately separate. The read bind is a scoped, read-only account. The
            write bind is only needed for the three attribute-write operations and should be
            delegated <strong>write-property only</strong> — never Domain Admin.
          </Typography>
          <Cmd
            label="Password files: mode 0600 BEFORE the secret is written"
            text={`sudo install -o samba-agent -g samba-agent -m 0600 /dev/null /etc/samba-agent/bind.pw
sudo tee /etc/samba-agent/bind.pw <<< 'READ_ONLY_BIND_PASSWORD'`}
          />
          <Cmd
            label="/etc/samba-agent/config.toml"
            text={`ldap_uri            = "ldaps://127.0.0.1:636"
base_dn             = "DC=example,DC=internal"
bind_dn             = "svc-agent-read@example.internal"
bind_password_file  = "/etc/samba-agent/bind.pw"
service_unit        = "samba-ad-dc"
ldap_insecure_tls   = true   # DC presents a private CA cert on loopback
hub_ca_cert         = "/etc/samba-agent/hub-ca.crt"

# OPTIONAL - only for update_user / set_user_workstations /
# set_user_custom_attribute. Omit and those refuse loudly.
write_bind_dn            = "svc-agent-write@example.internal"
write_bind_password_file = "/etc/samba-agent/write-bind.pw"`}
          />
        </>
      ),
    },
    {
      label: 'Install the sudoers allowlist',
      body: (
        <>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            The second half of the privilege model. Validate before installing — a broken
            sudoers file can lock you out of the host.
          </Typography>
          <Cmd
            text={`sudo visudo -cf sudoers-samba-agent \\
  && sudo install -o root -g root -m 0440 sudoers-samba-agent /etc/sudoers.d/samba-agent
sudo -l -U samba-agent`}
          />
          <Typography variant="caption" color="text.secondary">
            That last command, not this page, is the authority on what the agent may run.
          </Typography>
        </>
      ),
    },
    {
      label: 'Trust the hub',
      body: (
        <>
          <Typography variant="body2" sx={{ mb: 1.5 }}>
            The agent pins the control-plane CA, so the channel needs no public CA and must not
            traverse a TLS-inspecting proxy.
          </Typography>
          <Cmd text={`sudo tee /etc/samba-agent/hub-ca.crt   # paste the control-plane CA certificate
sudo chmod 644 /etc/samba-agent/hub-ca.crt`} />
        </>
      ),
    },
    {
      label: 'Enroll it — do this last',
      body: (
        <>
          <Alert severity="warning" sx={{ mb: 1.5 }}>
            <AlertTitle>Mint the token only when the host is ready</AlertTitle>
            The enrollment token is <strong>single-use</strong> and expires. Generate it on the
            Agents page with <strong>Enroll DC</strong>, then run this within the window.
          </Alert>
          <Cmd
            text={`sudo -u samba-agent env \\
  SAMBA_AGENT_HUB=${hub} \\
  SAMBA_AGENT_ENROLL_TOKEN=<token from the Agents page> \\
  SAMBA_AGENT_TOKEN_FILE=/etc/samba-agent/agent.token \\
  /usr/local/bin/samba-agent run --wait-secs 25`}
          />
          <Typography variant="caption" color="text.secondary">
            On success it stores its own long-lived token 0600 and reuses it across restarts.
          </Typography>
        </>
      ),
    },
    {
      label: 'Make it survive a reboot, then verify',
      body: (
        <>
          <Cmd
            text={`sudo systemctl enable --now samba-agent
systemctl is-enabled samba-agent && systemctl is-active samba-agent

# It must hold NO listening socket - the channel is outbound only:
sudo ss -tlnp | grep -c samba-agent   # expect 0`}
          />
          <Typography variant="body2">
            The new DC then appears in the selector at the top of this page, and selecting it
            points every screen at that directory.
          </Typography>
        </>
      ),
    },
  ];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth scroll="paper">
      <DialogTitle>Deploy the agent to a new domain controller</DialogTitle>
      <DialogContent dividers>
        <Alert severity="info" sx={{ mb: 2 }}>
          The agent <strong>dials out</strong>. The domain controller opens no inbound port, and
          this application never holds a credential for it — it sends a named operation from a
          fixed allowlist, never a command.
        </Alert>

        <Stepper orientation="vertical" nonLinear activeStep={-1}>
          {steps.map((s, i) => (
            <Step key={s.label} active expanded>
              <StepLabel>
                <Typography variant="subtitle2">{s.label}</Typography>
              </StepLabel>
              <StepContent>
                <Box sx={{ pb: 1 }}>{s.body}</Box>
                {i < steps.length - 1 && <Divider />}
              </StepContent>
            </Step>
          ))}
        </Stepper>

        {!hubUrl && (
          <Alert severity="error" sx={{ mt: 2 }}>
            This deployment has not declared <code>AGENT_HUB_URL</code>, so the address above is
            a placeholder. Set it on the backend to the TLS endpoint that serves{' '}
            <code>/api/agent/</code> — it is deliberately not the address of this web UI.
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}

export default AgentDeployGuide;
